// ─────────────────────────────────────────────────────────────
// 枚举
// ─────────────────────────────────────────────────────────────

export enum RoutingStrategy {
  WEIGHTED_RANDOM = 'weighted_random',
  LEAST_CONCURRENT = 'least_concurrent',
  ROUND_ROBIN = 'round_robin',
  FAILOVER = 'failover',
}

export enum HealthStatus {
  HEALTHY = 'healthy',
  DEGRADED = 'degraded',
  DOWN = 'down',
}

export enum ModelProtocol {
  OPENAI_COMPLETIONS = 'openai-completions',
  OPENAI_RESPONSES = 'openai-responses',
  ANTHROPIC_MESSAGES = 'anthropic-messages',
  TAVILY = 'tavily',
  GPT_IMAGE_2 = 'gpt-image-2',
  A2E_GPT_IMAGE_2 = 'a2e-gpt-image-2',
  Z_IMAGE = 'z-image',
}

export enum ModelCapabilityType {
  LLM = 'llm',
  OCR = 'ocr',
  ASR = 'asr',
  RETRIEVAL = 'retrieval',
  IMAGE_GENERATION = 'image-generation',
}

export enum ModelProvider {
  OPENAI = 'openai',
  ANTHROPIC = 'anthropic',
  DEEPSEEK = 'deepseek',
  TAVILY = 'tavily',
  GOOGLE = 'google',
  AZURE = 'azure',
  MOONSHOT = 'moonshot',
  QWEN = 'qwen',
  SILICONFLOW = 'siliconflow',
  ZHIPU = 'zhipu',
  BAIDU = 'baidu',
  VOLCENGINE = 'volcengine',
  OTHER = 'other',
}

export const MODEL_CAPABILITY_OPTIONS = [
  { value: ModelCapabilityType.LLM, label: 'LLM' },
  { value: ModelCapabilityType.OCR, label: 'OCR' },
  { value: ModelCapabilityType.ASR, label: 'ASR' },
  { value: ModelCapabilityType.RETRIEVAL, label: '检索' },
  { value: ModelCapabilityType.IMAGE_GENERATION, label: '图片生成' },
] as const;

export const MODEL_PROTOCOL_OPTIONS = [
  { value: ModelProtocol.OPENAI_COMPLETIONS, label: 'openai-completions' },
  { value: ModelProtocol.OPENAI_RESPONSES, label: 'openai-responses' },
  { value: ModelProtocol.ANTHROPIC_MESSAGES, label: 'anthropic-messages' },
  { value: ModelProtocol.TAVILY, label: 'tavily' },
  { value: ModelProtocol.GPT_IMAGE_2, label: 'gpt-image-2' },
  { value: ModelProtocol.A2E_GPT_IMAGE_2, label: 'a2e-gpt-image-2' },
  { value: ModelProtocol.Z_IMAGE, label: 'z-image' },
] as const;

export const MODEL_CAPABILITY_PROTOCOLS: Record<ModelCapabilityType, readonly ModelProtocol[]> = {
  [ModelCapabilityType.LLM]: [
    ModelProtocol.OPENAI_COMPLETIONS,
    ModelProtocol.OPENAI_RESPONSES,
    ModelProtocol.ANTHROPIC_MESSAGES,
  ],
  [ModelCapabilityType.OCR]: [
    ModelProtocol.OPENAI_COMPLETIONS,
    ModelProtocol.OPENAI_RESPONSES,
    ModelProtocol.ANTHROPIC_MESSAGES,
  ],
  [ModelCapabilityType.ASR]: [
    ModelProtocol.OPENAI_COMPLETIONS,
    ModelProtocol.OPENAI_RESPONSES,
  ],
  [ModelCapabilityType.RETRIEVAL]: [ModelProtocol.TAVILY],
  [ModelCapabilityType.IMAGE_GENERATION]: [
    ModelProtocol.GPT_IMAGE_2,
    ModelProtocol.A2E_GPT_IMAGE_2,
    ModelProtocol.Z_IMAGE,
  ],
} as const;

export const MODEL_PROVIDER_OPTIONS = [
  { value: ModelProvider.OPENAI, label: 'OpenAI' },
  { value: ModelProvider.ANTHROPIC, label: 'Anthropic' },
  { value: ModelProvider.DEEPSEEK, label: 'DeepSeek' },
  { value: ModelProvider.TAVILY, label: 'Tavily' },
  { value: ModelProvider.GOOGLE, label: 'Google' },
  { value: ModelProvider.AZURE, label: 'Azure OpenAI' },
  { value: ModelProvider.MOONSHOT, label: 'Moonshot' },
  { value: ModelProvider.QWEN, label: 'Qwen' },
  { value: ModelProvider.SILICONFLOW, label: 'SiliconFlow' },
  { value: ModelProvider.ZHIPU, label: 'Zhipu AI' },
  { value: ModelProvider.BAIDU, label: 'Baidu' },
  { value: ModelProvider.VOLCENGINE, label: 'Volcengine' },
  { value: ModelProvider.OTHER, label: '其他' },
] as const;

export type ModelInputModality = 'text' | 'image';

export const MODEL_INPUT_MODALITY_OPTIONS = [
  { value: 'text', label: '文本' },
  { value: 'image', label: '图片' },
] as const satisfies readonly { value: ModelInputModality; label: string }[];

export const MODEL_REASONING_EFFORT_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'XHigh' },
  { value: 'max', label: 'Max' },
] as const;

export interface ModelReasoningEffortInfo {
  id: string;
  name: string;
  description?: string;
}

export interface ModelReasoningInfo {
  efforts: ModelReasoningEffortInfo[];
  defaultEffort?: string;
}

export interface ModelCapabilityMetadata {
  inputModalities?: ModelInputModality[];
  contextWindow?: number;
  maxTokens?: number;
  maxReferenceImages?: number;
  reasoning?: ModelReasoningInfo;
}

const MODEL_PROVIDER_LABELS: Record<ModelProvider, string> = MODEL_PROVIDER_OPTIONS.reduce(
  (labels, option) => {
    labels[option.value] = option.label;
    return labels;
  },
  {} as Record<ModelProvider, string>,
);

const MODEL_PROTOCOL_LABELS: Record<ModelProtocol, string> = MODEL_PROTOCOL_OPTIONS.reduce(
  (labels, option) => {
    labels[option.value] = option.label;
    return labels;
  },
  {} as Record<ModelProtocol, string>,
);

export function normalizeModelProvider(provider?: string | null): ModelProvider {
  if (!provider) return ModelProvider.OTHER;
  const normalized = provider.trim().toLowerCase();
  return (Object.values(ModelProvider) as string[]).includes(normalized)
    ? (normalized as ModelProvider)
    : ModelProvider.OTHER;
}

export function normalizeModelProtocol(protocol?: string | null): ModelProtocol | undefined {
  if (!protocol) return undefined;
  const normalized = protocol.trim().toLowerCase();
  if (normalized === 'openai_chat_completions') {
    return ModelProtocol.OPENAI_COMPLETIONS;
  }
  return (Object.values(ModelProtocol) as string[]).includes(normalized)
    ? (normalized as ModelProtocol)
    : undefined;
}

export function getModelProviderLabel(provider?: string | null): string {
  return MODEL_PROVIDER_LABELS[normalizeModelProvider(provider)];
}

export function getModelProtocolLabel(protocol?: string | null): string {
  if (!protocol) return 'unknown';
  const normalized = normalizeModelProtocol(protocol);
  return (normalized && MODEL_PROTOCOL_LABELS[normalized]) || protocol;
}

export function getModelProtocolOptionsForCapability(capabilityType?: string | null) {
  const capability = capabilityType as ModelCapabilityType;
  const allowed = MODEL_CAPABILITY_PROTOCOLS[capability] || MODEL_CAPABILITY_PROTOCOLS[ModelCapabilityType.LLM];
  return MODEL_PROTOCOL_OPTIONS.filter((option) => allowed.includes(option.value));
}

export function isModelProtocolAllowedForCapability(
  capabilityType: string | null | undefined,
  protocol: ModelProtocol | null | undefined,
): boolean {
  if (!capabilityType || !protocol) return false;
  const allowed = MODEL_CAPABILITY_PROTOCOLS[capabilityType as ModelCapabilityType];
  return Boolean(allowed?.includes(protocol));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function positiveInteger(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : undefined;
}

function nonNegativeInteger(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}

function normalizeInputModalities(value: unknown): ModelInputModality[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const modalities = value.filter((item): item is ModelInputModality => item === 'text' || item === 'image');
  return modalities.length > 0 ? Array.from(new Set(modalities)) : undefined;
}

function normalizeReasoningEfforts(value: unknown): ModelReasoningEffortInfo[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const seen = new Set<string>();
  const efforts: ModelReasoningEffortInfo[] = [];
  for (const item of value) {
    if (typeof item === 'string' && item.trim()) {
      const id = item.trim();
      if (!seen.has(id)) {
        seen.add(id);
        efforts.push({ id, name: id.charAt(0).toUpperCase() + id.slice(1) });
      }
      continue;
    }
    if (!isRecord(item) || typeof item.id !== 'string' || !item.id.trim()) continue;
    const id = item.id.trim();
    if (seen.has(id)) continue;
    seen.add(id);
    efforts.push({
      id,
      name: typeof item.name === 'string' && item.name.trim()
        ? item.name.trim()
        : id.charAt(0).toUpperCase() + id.slice(1),
      ...(typeof item.description === 'string' && item.description.trim()
        ? { description: item.description.trim() }
        : {}),
    });
  }
  return efforts.length > 0 ? efforts : undefined;
}

export function modelCapabilityMetadata(defaultParams?: Record<string, unknown> | null): ModelCapabilityMetadata {
  if (!isRecord(defaultParams)) return {};
  const inputModalities = normalizeInputModalities(defaultParams.inputModalities);
  const contextWindow = positiveInteger(defaultParams.contextWindow);
  const maxTokens = positiveInteger(defaultParams.maxTokens);
  const maxReferenceImages = nonNegativeInteger(defaultParams.maxReferenceImages);
  const rawReasoning = isRecord(defaultParams.reasoning) ? defaultParams.reasoning : undefined;
  const efforts = normalizeReasoningEfforts(rawReasoning?.efforts);
  const defaultEffort = typeof rawReasoning?.defaultEffort === 'string' && rawReasoning.defaultEffort.trim()
    ? rawReasoning.defaultEffort.trim()
    : undefined;
  return {
    ...(inputModalities === undefined ? {} : { inputModalities }),
    ...(contextWindow === undefined ? {} : { contextWindow }),
    ...(maxTokens === undefined ? {} : { maxTokens }),
    ...(maxReferenceImages === undefined ? {} : { maxReferenceImages }),
    ...(efforts === undefined
      ? {}
      : {
        reasoning: {
          efforts,
          ...(defaultEffort !== undefined && efforts.some((effort) => effort.id === defaultEffort)
            ? { defaultEffort }
            : {}),
        },
      }),
  };
}

export function defaultMaxReferenceImagesForProtocol(protocol?: string | null): number | undefined {
  if (!protocol) return undefined;
  switch (protocol.trim().toLowerCase()) {
    case ModelProtocol.GPT_IMAGE_2:
      return 8;
    case ModelProtocol.A2E_GPT_IMAGE_2:
      return 8;
    case ModelProtocol.Z_IMAGE:
      return 0;
    default:
      return undefined;
  }
}

export function resolveModelMaxReferenceImages(
  defaultParams?: Record<string, unknown> | null,
  protocol?: string | null,
): number | undefined {
  const metadata = modelCapabilityMetadata(defaultParams);
  return metadata.maxReferenceImages ?? defaultMaxReferenceImagesForProtocol(protocol);
}

// ─────────────────────────────────────────────────────────────
// 模型定义
// ─────────────────────────────────────────────────────────────

export interface ModelDefinitionResponse {
  id: string;
  name: string;
  capabilityType: string;
  protocol: ModelProtocol;
  provider: ModelProvider;
  description: string | null;
  modelName: string | null;
  defaultParams: Record<string, any> | null;
  inputModalities?: ModelInputModality[];
  contextWindow?: number;
  maxTokens?: number;
  maxReferenceImages?: number;
  reasoning?: ModelReasoningInfo;
  routingStrategy: RoutingStrategy;
  retryOnFail: boolean;
  creditPrice: number;
  boundRoleIds: string[];
  boundRoles?: Array<{ id: string; name: string; code: string }>;
  isEnabled: boolean;
  isDefault: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date | null;
}

export interface CreateModelDefinitionRequest {
  name: string;
  capabilityType: string;
  protocol: ModelProtocol;
  provider: ModelProvider;
  description?: string;
  modelName?: string;
  defaultParams?: Record<string, any>;
  routingStrategy?: RoutingStrategy;
  retryOnFail?: boolean;
  creditPrice?: number;
  boundRoleIds?: string[];
  isEnabled?: boolean;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface UpdateModelDefinitionRequest {
  name?: string;
  capabilityType?: string;
  protocol?: ModelProtocol;
  provider?: ModelProvider;
  modelName?: string;
  description?: string;
  defaultParams?: Record<string, any>;
  routingStrategy?: RoutingStrategy;
  retryOnFail?: boolean;
  creditPrice?: number;
  boundRoleIds?: string[];
  isEnabled?: boolean;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface ModelDefinitionQueryParams {
  current?: number;
  size?: number;
  capabilityType?: string;
  protocol?: ModelProtocol;
  provider?: ModelProvider;
  isEnabled?: boolean;
}

// ─────────────────────────────────────────────────────────────
// 模型端点
// ─────────────────────────────────────────────────────────────

export interface ModelEndpointResponse {
  id: string;
  modelId: string;
  name: string;
  basePath: string;
  apiKey: string;
  apiSecret: string | null;
  modelNameOverride: string | null;
  concurrencyLimit: number;
  weight: number;
  timeoutMs: number;
  maxRetries: number;
  healthStatus: HealthStatus;
  lastError: string | null;
  lastErrorAt: string | null;
  successCount: number;
  errorCount: number;
  extraHeaders: Record<string, any> | null;
  extraConfig: ModelEndpointExtraConfig | null;
  isEnabled: boolean;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date | null;
}

export interface ModelEndpointExtraConfig {
  proxyUrl?: string;
  [key: string]: any;
}

export interface CreateModelEndpointRequest {
  name: string;
  basePath: string;
  apiKey: string;
  apiSecret?: string;
  modelNameOverride?: string | null;
  concurrencyLimit?: number;
  weight?: number;
  timeoutMs?: number;
  maxRetries?: number;
  extraHeaders?: Record<string, any>;
  extraConfig?: ModelEndpointExtraConfig;
  isEnabled?: boolean;
}

export interface UpdateModelEndpointRequest {
  name?: string;
  basePath?: string;
  apiKey?: string;
  apiSecret?: string;
  modelNameOverride?: string | null;
  concurrencyLimit?: number;
  weight?: number;
  timeoutMs?: number;
  maxRetries?: number;
  extraHeaders?: Record<string, any>;
  extraConfig?: ModelEndpointExtraConfig;
  isEnabled?: boolean;
}

// ─────────────────────────────────────────────────────────────
// 健康状态响应
// ─────────────────────────────────────────────────────────────

export interface HealthStatusResponse {
  endpointId: string;
  status: HealthStatus;
  latencyMs: number;
  lastCheckAt: string | null;
  errorRate: number;
  successCount: number;
  errorCount: number;
}
